// src/pages/BookArticlesPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { FEATURED_ARTICLES } from "../data/featuredArticles";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";

import { api, fetchJsonWithProxies } from "../lib/omekaClient";
import { titleOf, descOf } from "../lib/textOf";

/* --------------------------------------------------------------------------
  เงื่อนไขสำหรับดึง "ทรัพยากรสารสนเทศ" จาก Omeka S มาแสดงในหน้านี้

  วิธีใช้งานในหลังบ้าน Omeka S:
  1) เปิด Item เล่มที่ต้องการให้มาโชว์ในหน้านี้
  2) เพิ่มฟิลด์ dcterms:audience
  3) ใส่ค่าเป็น book-articles
  4) Save
---------------------------------------------------------------------------- */
const OMEKA_FEATURE_ITEM_SET_ID = "619";
const OMEKA_FEATURE_LIMIT = 100;

/* --- Helpers --- */
const toHttps = (u) =>
  u ? (u.startsWith("http://") ? u.replace(/^http:/, "https:") : u) : "";

const stripTags = (html = "") => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

const buildApiUrlWithQuery = (baseUrl, queryObject = {}) => {
  const params = new URLSearchParams();

  Object.entries(queryObject).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.append(key, value);
  });

  const query = params.toString();
  if (!query) return baseUrl;

  return `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${query}`;
};

const fetchFeaturedOmekaResources = async () => {
  const url = buildApiUrlWithQuery(api("/items"), {
    item_set_id: OMEKA_FEATURE_ITEM_SET_ID,
    limit: OMEKA_FEATURE_LIMIT,
    sort_by: "created",
    sort_order: "desc",
  });

  const data = await fetchJsonWithProxies(url);
  return Array.isArray(data) ? data : [];
};

export default function BookArticlesPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  /* --- Pagination States --- */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const omekaResources = await fetchFeaturedOmekaResources();

        const filteredOmeka = (omekaResources || []).map((it) => ({
          ...it,
          __kind: "omeka",
        }));

        /*
          TODO:
          ตอนนี้บทความ/นิทรรศการที่ทำไว้ฝั่งหน้าบ้านยังดึงจาก
          src/data/featuredArticles.js เหมือนเดิมก่อน

          ถ้านำบทความเหล่านี้ไปจัดเก็บใน Omeka S ครบแล้ว
          ค่อยลบ FEATURED_ARTICLES, staticItems และเงื่อนไข __kind === "static"
          ออกจากไฟล์นี้ในภายหลัง
        */
        const staticItems = FEATURED_ARTICLES.map((fa) => ({
          ...fa,
          __kind: "static",
        }));

        if (alive) setItems([...staticItems, ...filteredOmeka]);
      } catch (e) {
        console.error("Failed to load articles", e);

        /*
          ถ้า Omeka S โหลดไม่ได้ ให้ยังแสดงบทความที่อยู่ฝั่งหน้าบ้านไว้ก่อน
          เพื่อไม่ให้หน้าเว็บว่างทั้งหมด
        */
        const staticItems = FEATURED_ARTICLES.map((fa) => ({
          ...fa,
          __kind: "static",
        }));

        if (alive) setItems(staticItems);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [i18n.language, t]);

  /* --- Pagination Logic --- */
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(items.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#fcfaf8] text-[#3d342e] font-sans">
      <Helmet>
        <title>{t("sections.recommendedKnowledge", "องค์ความรู้")}</title>
        <meta
          name="description"
          content={t(
            "meta.articlesDescription",
            "คลังความรู้เรื่องการอนุรักษ์ใบลานและมรดกวัฒนธรรมล้านนา"
          )}
        />
      </Helmet>

      <SiteHeader />

      <main className="pt-24 pb-20 px-4 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-10 flex items-center gap-3">
          <span className="w-8 h-[2px] bg-[#d8653b]"></span>
          {t("sections.recommendedKnowledge", "องค์ความรู้")}
        </h1>

        {/* --- Article Grid --- */}
        <AnimatePresence mode="wait">
          {loading ? (
            <SkeletonGrid key="skeleton" />
          ) : (
            <>
              <motion.div
                key={`page-${currentPage}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {currentItems.map((item) => (
                  <ArticleCard key={item.id || item["o:id"] || item.path} item={item} />
                ))}
              </motion.div>

              {/* --- Pagination UI --- */}
              {totalPages > 1 && (
                <div className="mt-16 flex justify-center items-center gap-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="h-10 px-4 rounded-lg border border-[#e5d7ca] text-sm font-bold text-[#7b6c61] disabled:opacity-20 transition-colors hover:bg-[#f1e6da]"
                  >
                    {t("bookArticles.pagination.prev", "ก่อนหน้า")}
                  </button>

                  <div className="flex gap-2">
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => handlePageChange(i + 1)}
                        className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${
                          currentPage === i + 1
                            ? "bg-[#d8653b] text-white border-[#d8653b]"
                            : "bg-white border border-[#e5d7ca] text-[#7b6c61] hover:bg-[#f1e6da]"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="h-10 px-4 rounded-lg border border-[#e5d7ca] text-sm font-bold text-[#7b6c61] disabled:opacity-20 transition-colors hover:bg-[#f1e6da]"
                  >
                    {t("bookArticles.pagination.next", "ถัดไป")}
                  </button>
                </div>
              )}
            </>
          )}
        </AnimatePresence>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}

/* --- Components --- */

function ArticleCard({ item }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const title =
    item.__kind === "static"
      ? t(item.titleKey)
      : titleOf(item);

  const rawDesc =
    item.__kind === "static"
      ? t(item.descKey)
      : stripTags(descOf(item));

  const cover =
    item.__kind === "static"
      ? item.cover
      : toHttps(item.thumbnail_display_urls?.medium || item.thumbnail_display_urls?.large);

  return (
    <motion.div
      layout
      className="bg-white rounded-2xl overflow-hidden border border-[#e5d7ca] hover:shadow-2xl transition-all group flex flex-col"
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={cover || "/assets/placeholder.webp"}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
      </div>

      <div className="p-6 flex flex-col flex-grow">
        <h3 className="text-xl font-bold mb-3 text-[#3d342e] line-clamp-2 leading-snug group-hover:text-[#d8653b] transition-colors italic">
          {title}
        </h3>

        <p className="text-[#7b6c61] text-sm line-clamp-3 mb-6 font-light leading-relaxed">
          {rawDesc || t("bookArticles.emptyDescription", "คลิกเพื่อดูรายละเอียดของทรัพยากรสารสนเทศรายการนี้")}
        </p>

        <div className="mt-auto pt-4 border-t border-[#f1e6da] flex items-center justify-between">
          <div className="flex items-center gap-2 text-[10px] text-[#9a8a7e] font-bold">
            
          </div>

          <button
            onClick={() => navigate(item.__kind === "static" ? item.path : `/book/${item["o:id"]}`)}
            className="text-[#d8653b] font-bold text-sm flex items-center gap-1 group-hover:translate-x-1 transition-transform"
          >
            {t("actions.read", "อ่าน")} <span className="text-lg">›</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="bg-white rounded-2xl h-[450px] animate-pulse border border-[#e5d7ca]"
        >
          <div className="h-56 bg-[#f1e6da] rounded-t-2xl" />
          <div className="p-6">
            <div className="h-6 w-full bg-[#f1e6da] rounded mb-2" />
            <div className="h-4 w-full bg-[#f1e6da] rounded mb-6" />
            <div className="h-20 w-full bg-[#f1e6da] rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}